/* eslint-disable tsdoc/syntax */
import { forwardRef, useEffect, useRef } from "react";
import clsx from "clsx";

type CheckboxState = "unchecked" | "checked" | "indeterminate";

type CheckboxProps = {
  state?: CheckboxState;
  variant?: "binary" | "triState";
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;

  label?: string;
  title?: string;
  description?: string;

  onChange?: (nextState: CheckboxState) => void;
  className?: string;
};

export const CheckBox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      state = "unchecked",
      variant = "binary",
      disabled,
      readOnly,
      error,
      label,
      title,
      description,
      onChange,
      className,
    },
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const resolvedRef = (ref ||
      internalRef) as React.RefObject<HTMLInputElement>;

    const isChecked = state === "checked";
    const isIndeterminate = state === "indeterminate";
    const isReadOnly = readOnly && !disabled;
    const isDisabled = disabled;

    const isSelected = isChecked || isIndeterminate;
    const showErrorBorder = error && !isSelected;

    // set native indeterminate property
    useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = isIndeterminate;
      }
    }, [isIndeterminate]);

    const getNextState = (): CheckboxState => {
      if (variant === "binary") return isChecked ? "unchecked" : "checked";
      if (state === "unchecked") return "checked";
      if (state === "checked") return "indeterminate";
      return "unchecked";
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (disabled || readOnly) return;
      onChange?.(getNextState());
    };

    const iconColor = isDisabled ? "#9CA3AF" : isReadOnly ? "#878785" : "white";

    const boxClass = isDisabled
      ? "cursor-not-allowed border-neutral-100 bg-neutral-100"
      : clsx(
          !isSelected && "border-neutral-400 bg-white",
          isSelected && !isReadOnly && "border-primary-300 bg-primary-300",
          isSelected && isReadOnly && "border-neutral-300 bg-neutral-50",
          showErrorBorder && "border-primary-300",
          "cursor-pointer"
        );

    return (
      <div className={clsx("flex flex-col")}>
        <label
          className={clsx("flex select-none items-center gap-2", className)}
        >
          <input
            ref={resolvedRef}
            type="checkbox"
            checked={isChecked}
            disabled={disabled}
            readOnly
            onChange={handleChange}
            className="sr-only"
          />

          <div
            className={`relative flex h-[18px] w-[18px] items-center justify-center rounded-[4px] border transition-all duration-150 ${boxClass}`}
          >
            {/* CHECK */}
            {isChecked && (
              <svg
                viewBox="0 0 16 13"
                className="h-[10px] w-[12px]"
                fill="none"
              >
                <path
                  d="M1.5 7L6 11.5L14.5 1.5"
                  stroke={iconColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}

            {/* MINUS */}
            {isIndeterminate && (
              <svg viewBox="0 0 12 2" className="h-[2px] w-[10px]" fill="none">
                <path
                  d="M1 1H11"
                  stroke={iconColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>

          {(title || label) && (
            <div className="flex flex-col">
              {title && (
                <span
                  className={clsx(
                    "text-sm font-medium",
                    disabled ? "text-neutral-200" : "text-neutral-700"
                  )}
                >
                  {title}
                </span>
              )}

              {label && (
                <span
                  className={clsx(
                    "text-sm",
                    disabled ? "text-neutral-200" : "text-neutral-500"
                  )}
                >
                  {label}
                </span>
              )}
            </div>
          )}
        </label>

        {/* Description under whole component */}
        {description && (
          <span
            className={clsx(
              " mt-2 block text-xs",
              error ? "text-primary-300" : "text-neutral-500",
              disabled && "text-neutral-200"
            )}
          >
            {description}
          </span>
        )}
      </div>
    );
  }
);
